'use client';

import { useSearchParams, usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

import { CTASpotlightBanner } from '@/components/molecules';
import { CardCarousel, ContentCardVariant } from '@/components/organisms';

import type {
  CollectionCarouselProps,
  CardCarouselProps,
} from '@/components/organisms';

export const CollectionCarousel: React.FC<CollectionCarouselProps> = ({
  contentType,
  title,
  description,
  buttonLabel,
  buttonLink,
  imageLink,
  items,
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const prevPath = useRef<string | null>(null);
  const cardItems: CardCarouselProps['items'] = items.map((item, index) => {
    const isPodcast = item.rawData?.id;
    return {
      id: item.id,
      title: item.title,
      description: '',
      imageUrl: item.image,
      contentType: item.type,
      variant: ContentCardVariant.BASIC,
      episodeIndex: isPodcast ? index : undefined,
      rawData: isPodcast ? item.rawData : undefined,
    };
  });

  useEffect(() => {
    const currentPath = pathname + '?' + searchParams.toString();
    if (prevPath.current !== currentPath) {
      prevPath.current = currentPath;

      const storedId = sessionStorage.getItem('lastVisitedWidget');
      if (storedId) {
        const el = document.getElementById(`card-carousel-${storedId}`);
        if (el) {
          const yOffset = -80;
          const y = el.getBoundingClientRect().top + window.scrollY + yOffset;
          setTimeout(() => {
            window.scrollTo({ top: y, behavior: 'smooth' });
            sessionStorage.removeItem('lastVisitedWidget');
          }, 200);
        }
      }
    }
  }, [pathname, searchParams]);

  return (
    <div className="w-full bg-white overflow-hidden">
      <div className="px-1">
        <CTASpotlightBanner
          title={title}
          contentType={'Collection'}
          imageUrl={imageLink}
          content={description}
          primaryButtonText={buttonLabel}
          href={buttonLink}
          className="mb-4"
        />
      </div>

      {cardItems.length > 0 && (
        <div>
          <CardCarousel
            title={contentType}
            items={cardItems}
            variant="default"
            hideTitle={true}
          />
        </div>
      )}
    </div>
  );
};
